import React, { useEffect, useState } from "react";
import { api, rupee } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { Check } from "lucide-react";

function formatExpiry(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function daysLeft(iso) {
  if (!iso) return null;
  return Math.max(0, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));
}

function loadRazorpayScript() {
  return new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve();
      return;
    }
    const existing = document.querySelector('script[src="https://checkout.razorpay.com/v1/checkout.js"]');
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () => reject(new Error("Could not load Razorpay")));
      return;
    }
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("Could not load Razorpay"));
    document.body.appendChild(s);
  });
}

export default function Subscription() {
  const [plans, setPlans] = useState({});
  const { tenant, setTenant, user } = useAuth();
  const [busy, setBusy] = useState("");

  useEffect(() => {
    api.get("/subscription/plans").then((r) => {
      setPlans(r.data.plans || r.data);
    });
  }, []);

  const activatePlan = async (planKey, payment = null) => {
    const payload = { plan: planKey };
    if (payment) {
      payload.razorpay_order_id = payment.razorpay_order_id;
      payload.razorpay_payment_id = payment.razorpay_payment_id;
      payload.razorpay_signature = payment.razorpay_signature;
    }
    const { data: sub } = await api.post("/subscription/activate", payload);
    setTenant({ ...tenant, subscription: sub.subscription });
    return sub;
  };

  const upgrade = async (planKey) => {
    setBusy(planKey);
    try {
      const { data } = await api.post("/subscription/create-order", { plan: planKey });

      if (data.demo) {
        await activatePlan(planKey);
        toast.success(`${plans[planKey].name} plan activated (demo mode)`);
        return;
      }

      await loadRazorpayScript();

      const orderId = data.order_id;

      const reportPaymentFailed = async (reason) => {
        try {
          await api.post("/subscription/payment-failed", {
            plan: planKey,
            order_id: orderId,
            reason: reason || "Payment failed",
          });
        } catch {
          /* email is best-effort */
        }
      };

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency || "INR",
        order_id: data.order_id,
        name: "VastraKiran",
        description: `${data.plan.name} subscription · 30 days`,
        prefill: {
          name: user?.name || "",
          email: user?.email || "",
        },
        theme: { color: "#8B1E3F" },
        handler: async (response) => {
          try {
            const sub = await activatePlan(planKey, response);
            const renewed =
              tenant?.subscription?.status === "active" && tenant?.subscription?.plan === planKey;
            toast.success(
              renewed
                ? "Subscription renewed for 30 more days"
                : `${plans[planKey]?.name || planKey} plan activated until ${formatExpiry(sub.subscription.expires_at)}`
            );
          } catch (err) {
            const detail = err?.response?.data?.detail || "Payment received but activation failed — contact support";
            toast.error(detail);
            await reportPaymentFailed(detail);
          }
        },
        modal: {
          ondismiss: () => toast.info("Payment cancelled"),
        },
      };

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", async (res) => {
        const reason = res?.error?.description || res?.error?.reason || "Payment failed";
        toast.error(reason);
        await reportPaymentFailed(reason);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start payment");
    } finally {
      setBusy("");
    }
  };

  const sub = tenant?.subscription;
  const isTrial = sub?.status === "trial";
  const isActive = sub?.status === "active";
  const currentPlan = isActive ? sub?.plan : null;
  const expiry = sub?.expires_at;
  const remaining = daysLeft(expiry);

  const planButtonLabel = (k, p) => {
    if (isActive && currentPlan === k) return busy === k ? "Processing…" : "Renew 30 days";
    if (busy === k) return "Processing…";
    if (isTrial) return `Choose ${p.name}`;
    if (isActive) return `Switch to ${p.name}`;
    return `Subscribe · ${p.name}`;
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto" data-testid="subscription-root">
      <h1 className="font-display font-bold text-3xl">Subscription</h1>

      {isTrial && (
        <div className="mt-4 rounded-lg px-4 py-3 text-sm" style={{ background: "var(--surface-alert)", color: "var(--text-main)" }}>
          <b>Free trial</b>
          {remaining != null && <span> · {remaining} day{remaining === 1 ? "" : "s"} left</span>}
          {expiry && <span> · ends {formatExpiry(expiry)}</span>}
          <p className="mt-1" style={{ color: "var(--text-muted)" }}>
            You have full access during the trial. Choose a plan below before it ends.
          </p>
        </div>
      )}

      {isActive && currentPlan && (
        <p className="text-sm mt-4" style={{ color: "var(--text-muted)" }}>
          Current plan: <b className="capitalize text-[var(--text-main)]">{currentPlan}</b>
          {expiry && (
            <span>
              {" "}
              · valid until {formatExpiry(expiry)}
              {remaining != null && remaining <= 7 && (
                <span style={{ color: "var(--error)" }}> · renew soon ({remaining} day{remaining === 1 ? "" : "s"} left)</span>
              )}
            </span>
          )}
        </p>
      )}

      {!isTrial && !isActive && (
        <p className="text-sm mt-4" style={{ color: "var(--text-muted)" }}>
          No active subscription. Choose a plan below.
        </p>
      )}

      <div className="mt-8 grid md:grid-cols-3 gap-5">
        {Object.entries(plans).map(([k, p]) => {
          const isCurrent = isActive && currentPlan === k;
          return (
            <div
              key={k}
              className={`card-soft p-6 ${isCurrent ? "ring-2" : ""}`}
              style={isCurrent ? { borderColor: "var(--primary)", boxShadow: "0 8px 30px rgba(139,30,63,0.12)" } : {}}
              data-testid={`plan-${k}`}
            >
              <div className="font-display font-semibold text-xl">{p.name}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="font-display font-bold text-4xl num">{rupee(p.price_inr)}</span>
                <span className="text-sm" style={{ color: "var(--text-muted)" }}>/month</span>
              </div>
              <ul className="mt-4 space-y-2 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check size={16} style={{ color: "var(--success)" }} className="mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <button
                data-testid={`upgrade-${k}`}
                disabled={busy === k}
                onClick={() => upgrade(k)}
                className="mt-6 w-full h-12 rounded-md text-white font-semibold tactile-btn"
                style={{ background: "var(--primary)" }}
              >
                {planButtonLabel(k, p)}
              </button>
            </div>
          );
        })}
      </div>

      <p className="mt-6 text-xs" style={{ color: "var(--text-muted)" }}>
        Payments are processed securely by Razorpay (test mode). Renewing adds 30 days from your current expiry date.
      </p>
    </div>
  );
}
